import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Feedback } from '../../models/feedback.model';
import { TranslateService } from '@ngx-translate/core';
import { EnvService, GemValidators, TokenHelperService } from 'gematik-form-library';
import { Subscription } from 'rxjs';

@Component({
  selector: 'gem-feedback-general-form',
  styleUrls: ['gem-feedback-general-form.component.scss'],
  template: `
    <form [formGroup]="form">
      <div class="row mb-3">
        <div class="col-sm-12 mt-3">
          <p>{{ 'feedback.generalRating' | translate }}</p>
          <ngb-rating [max]="5" formControlName="rating" class="rating"></ngb-rating>
        </div>
        <div class="col-sm-12 mt-3">
          <gem-textarea
            [placeholder]="'feedback.generalComments' | translate"
            [control]="comments"
            [maxChars]="500"
            [hint]="translateService.instant('feedback.commentsHint') + ' (' + comments.value.length + ' / 500' + ')'"
          ></gem-textarea>
        </div>

        <div class="col-sm-12 mt-3 general-contact">
          <p>{{ 'feedback.generalContact' | translate }}</p>
          <gem-radio-button-group-vertical
            [valueLabels]="radioButtonOptions"
            [control]="contact"
          ></gem-radio-button-group-vertical>
        </div>

        <div *ngIf="email.enabled" class="col-sm-12 mt-3">
          <p>{{ 'feedback.generalEmailRequired' | translate }}</p>
          <gem-input [placeholder]="'email' | translate" [hint]="'feedback.emailHint' | translate" [control]="email">
          </gem-input>
        </div>

        <!-- <div *ngIf="!isLoggedIn" class="col-sm-12 mt-3"> -->
        <div *ngIf="email.enabled" class="col-sm-12 mt-3">
          <div class="checkbox-container disable-checkbox">
            <mat-checkbox [formControl]="dataPrivacy" [color]="'primary'">
              <span style="cursor: default" [innerHTML]="dataPrivacyLabel"></span>
            </mat-checkbox>
          </div>
        </div>

        <div class="col-sm-12 text-end mt-3">
          <button
            type="button"
            class="mat-focus-indicator btn btn-primary mat-button mat-button-base"
            style="display: inline-flex"
            cdkFocusInitial
            [disabled]="!isFormValid() || isLoading"
            (click)="onSubmit($event)"
          >
            {{ 'feedback.send' | translate }}
            <mat-icon iconPositionEnd class="ms-2">send</mat-icon>
          </button>
        </div>
      </div>
    </form>
  `,
  standalone: false,
})
export class GemFeedbackGeneralFormComponent implements OnInit, OnDestroy {
  @Input() isLoading: boolean = false;
  @Output() back: EventEmitter<any> = new EventEmitter();
  @Output() feedback: EventEmitter<{ feedback: Feedback; documents: any[] }> = new EventEmitter();

  form = this.fb.group({
    category: ['GENERAL', Validators.required],
    rating: [undefined, Validators.required],
    comments: [''],
    contact: ['ANONYMOUS'],
    email: [{ value: undefined, disabled: true }],
    dataPrivacy: [false],
  });

  radioButtonOptions = [
    { value: 'ANONYMOUS', label: this.translateService.instant('feedback.radioButtonAnonymous') },
    { value: 'WITH_EMAIL', label: this.translateService.instant('feedback.radioButtonWithEmail') },
  ];

  subscriptions: Subscription[] = [];

  constructor(
    private fb: FormBuilder,
    public translateService: TranslateService,
    private tokenHelperService: TokenHelperService,
    private env: EnvService,
  ) {}

  ngOnInit(): void {
    const sub = this.contact?.valueChanges.subscribe(change => {
      if (change === 'WITH_EMAIL') {
        this.email?.addValidators([Validators.required, GemValidators.email]);
        this.email?.updateValueAndValidity();
        this.email.enable();
        if (this.tokenHelperService.isLoggedIn()) {
          this.email?.setValue(this.tokenHelperService.getEmail());
        }
        this.dataPrivacy.addValidators(Validators.requiredTrue);
        this.dataPrivacy.updateValueAndValidity();
      } else {
        this.email?.clearValidators();
        this.email?.updateValueAndValidity();
        this.email?.reset();
        this.email.disable();
        this.dataPrivacy.clearValidators();
        this.dataPrivacy.updateValueAndValidity();
      }
    });
    this.subscriptions.push(sub);
    if (this.tokenHelperService.isLoggedIn()) {
      this.dataPrivacy.setValue(true);
    } else {
      this.dataPrivacy.setValue(false);
    }
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  onBack(): void {
    this.back.emit();
  }

  onSubmit(event: any): void {
    const feedback: Feedback = this.form.getRawValue();
    this.feedback.emit({ feedback: feedback, documents: [] });
    event.target.disabled = true;
  }

  get email(): FormControl {
    return this.form.get('email') as FormControl;
  }

  get emailIsRequired(): boolean {
    return this.email.hasValidator(Validators.required);
  }

  get dataPrivacy(): FormControl {
    return this.form.get('dataPrivacy') as FormControl;
  }

  get isLoggedIn(): boolean {
    return this.tokenHelperService.isLoggedIn();
  }

  get dataPrivacyLabel(): string {
    return this.translateService.instant('feedback.dataPrivacy', {
      link: `${this.env.uwlUrl}/data-privacy`,
    });
  }

  get comments(): FormControl {
    return this.form.get('comments') as FormControl;
  }

  get contact(): FormControl {
    return this.form.get('contact') as FormControl;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
